import { ChangeEventHandler, FC, FocusEventHandler, FormEventHandler, useState } from 'react';

import { TextArea as PurPurTextArea, TextAreaProps } from '@purpur/library';

import { useComponentId } from '@telialt/utils/src/hooks';

type Props = Omit<TextAreaProps, 'id' | 'errorText' | 'data-testid'> & {
    dataTest: string;
    requiredErrorMessage?: string;
    minLengthErrorMessage?: string;
    errorMessage?: string;
    id?: string;
};

export const TextArea: FC<Props> = ({
    defaultValue,
    required,
    requiredErrorMessage = '',
    minLengthErrorMessage = '',
    minLength,
    dataTest,
    onChange,
    onBlur,
    errorMessage,
    id,
    ...props
}) => {
    const generatedId = `text-area-${useComponentId()}`;

    const [errorText, setErrorText] = useState<string>('');
    const [isPristine, setIsPristine] = useState<boolean>(true);
    const [textAreaValue, setTextAreaValue] = useState<string>(String(defaultValue));

    const validateInput = (eventValue: string, isInputPristine: boolean = isPristine) => {
        if (!isInputPristine && required && eventValue === '') {
            setErrorText(requiredErrorMessage);
        } else if (!isInputPristine && minLength && eventValue.length < minLength) {
            setErrorText(minLengthErrorMessage);
        } else {
            setErrorText('');
        }
    };

    const onTextAreaBlur: FocusEventHandler<HTMLTextAreaElement> = (event) => {
        setIsPristine(false);
        setTextAreaValue(event.target.value);
        validateInput(event.target.value, false);
        onBlur?.(event);
    };

    const onTextAreaChange: ChangeEventHandler<HTMLTextAreaElement> = (event) => {
        setTextAreaValue(event.target.value);
        validateInput(event.target.value);
        onChange?.(event);
    };

    const onInvalidCheck: FormEventHandler<HTMLTextAreaElement> = (event) => {
        event.preventDefault();
        setIsPristine(false);
        validateInput(textAreaValue, false);
    };

    return (
        <PurPurTextArea
            {...props}
            id={id || generatedId}
            defaultValue={defaultValue}
            required={required}
            errorText={errorMessage || errorText}
            data-test={dataTest}
            onBlur={onTextAreaBlur}
            onChange={onTextAreaChange}
            onInvalid={onInvalidCheck}
        />
    );
};
